import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { money, STRIPE_TEST_MODE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PostcardCanvas from "@/components/PostcardCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";
import { Mail, MapPin, ArrowRight, Loader2, Sparkles, X, Pencil, GripVertical, Copy, Trophy } from "lucide-react";

const TYPE_LABEL = { premium: "4×9 Premium Hero", standard: "4×6 Standard", basic: "4×3 Basic" };

export default function Postcards() {
  const { user, openAuth } = useAuth();
  const [runs, setRuns] = useState([]);
  const [states, setStates] = useState([]);
  const [state, setState] = useState("MO");
  const [loading, setLoading] = useState(true);

  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);

  const [myBiz, setMyBiz] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ business_id: "", deal_headline: "", deal_details: "", ab: null, plan: "per_drop", terms: false, tos: false, consent: false });
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [newZip, setNewZip] = useState("");
  const [startBusy, setStartBusy] = useState(false);
  const startZip = async () => {
    if (!user) return openAuth("owner");
    if (!/^\d{5}$/.test(newZip.trim())) return toast.error("Enter a 5-digit ZIP code");
    setStartBusy(true);
    try {
      const { data } = await api.post("/postcard-runs/start-zip", { zip: newZip.trim(), business_id: myBiz[0]?.id });
      const r = data.run;
      setRuns((rs) => (rs.some((x) => x.id === r.id) ? rs : [...rs, { ...r, available_count: (r.slots || []).filter((s) => s.status === "available").length }]));
      setActiveRunId(r.id);
      toast.success(data.created ? `New card started for ${r.city}, ${r.state_abbr} ${r.central_zip} — pick your spot below. A local rep will help fill the rest.` : `${r.city} ${r.central_zip} already has a card — pick your spot below.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start a card for that ZIP"); }
    setStartBusy(false);
  };
  const [sp] = useSearchParams();
  const rebookId = sp.get("rebook");

  const loadMyBiz = useCallback(() => {
    if (user) return api.get("/my/businesses").then(({ data }) => setMyBiz(data.filter((b) => b.listing_status === "approved")));
    return Promise.resolve();
  }, [user]);

  const genDeal = async () => {
    const biz = myBiz.find((b) => b.id === form.business_id);
    if (!biz) return toast.error("Choose a business first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-ad", { business_id: biz.id });
      setForm((f) => ({ ...f, deal_headline: data.deal_headline || f.deal_headline, deal_details: data.deal_details || f.deal_details, ab: null }));
      await loadMyBiz();
      const winner = biz.ab_winner;
      if (winner && data.deal_id) {
        toast.success("Deal saved to your library", {
          description: `Variation ${winner} is winning — tag this new deal ${winner} to keep testing it?`,
          action: { label: `Tag as ${winner}`, onClick: () => { setForm((f) => ({ ...f, ab: winner })); setDealAB(data.deal_id, winner); } },
        });
      } else {
        toast.success("Deal drafted & saved to your deal library");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "AI failed"); }
    finally { setAiBusy(false); }
  };

  const deleteDeal = async (dealId) => {
    try {
      await api.delete(`/businesses/${form.business_id}/deals/${dealId}`);
      await loadMyBiz();
      toast.success("Deal removed from library");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove deal"); }
  };

  const [editDealId, setEditDealId] = useState(null);
  const [editName, setEditName] = useState("");
  const [dragId, setDragId] = useState(null);

  const startRename = (d) => { setEditDealId(d.id); setEditName(d.name || d.headline || ""); };

  const saveRename = async (dealId) => {
    const name = editName.trim();
    if (!name) { setEditDealId(null); return; }
    try {
      await api.patch(`/businesses/${form.business_id}/deals/${dealId}`, { name });
      await loadMyBiz();
      toast.success("Deal renamed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
    finally { setEditDealId(null); }
  };

  const reorderDeals = async (biz, fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const ds = [...(biz.ai_deals || [])];
    const fromIdx = ds.findIndex((d) => d.id === fromId);
    const toIdx = ds.findIndex((d) => d.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = ds.splice(fromIdx, 1);
    ds.splice(toIdx, 0, moved);
    setMyBiz((prev) => prev.map((b) => (b.id === biz.id ? { ...b, ai_deals: ds } : b)));
    try { await api.put(`/businesses/${biz.id}/deals/order`, { order: ds.map((d) => d.id) }); }
    catch { loadMyBiz(); }
    finally { setDragId(null); }
  };

  const duplicateDeal = async (dealId) => {
    try {
      await api.post(`/businesses/${form.business_id}/deals/${dealId}/duplicate`);
      await loadMyBiz();
      toast.success("Deal duplicated — edit the copy");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate"); }
  };

  const setDealAB = async (dealId, ab) => {
    try {
      await api.patch(`/businesses/${form.business_id}/deals/${dealId}/ab`, { ab });
      await loadMyBiz();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not tag"); }
  };

  useEffect(() => { api.get("/states").then(({ data }) => setStates(data.filter((s) => s.launch_status === "Active co-op routes"))); }, []);
  useEffect(() => { loadMyBiz(); }, [loadMyBiz]);

  useEffect(() => {
    setLoading(true);
    api.get("/postcard-runs", { params: state === "all" ? {} : { state } })
      .then(({ data }) => {
        setRuns(data);
        setActiveRunId(data.length ? data[0].id : null);
      })
      .finally(() => setLoading(false));
  }, [state]);

  const loadCard = useCallback((id) => {
    if (!id) { setActiveRun(null); return; }
    setLoadingCard(true);
    api.get(`/postcard-runs/${id}`).then(({ data }) => setActiveRun(data)).finally(() => setLoadingCard(false));
  }, []);

  useEffect(() => { loadCard(activeRunId); }, [activeRunId, loadCard]);

  useEffect(() => {
    if (rebookId && myBiz.length) {
      const b = myBiz.find((x) => x.id === rebookId);
      if (b) toast.message(`Re-booking ${b.name} — pick an open spot below to confirm your renewal.`);
    }
  }, [rebookId, myBiz]);

  const onSelect = (s) => {
    if (!user) return openAuth("owner");
    if (myBiz.length === 0) { toast.error("Add an approved business first from your dashboard."); return; }
    if (plans.length === 0) api.get("/postcard-plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
    setSlot(s);
    const b = (rebookId && myBiz.find((x) => x.id === rebookId)) || myBiz[0];
    setForm({ business_id: b.id, deal_headline: b.postcard_deal_headline || b.ai_deal_headline || "", deal_details: b.postcard_deal_details || b.ai_deal_details || "", ab: null, plan: "per_drop", terms: false, tos: false, consent: false });
    if (rebookId && b.id === rebookId) {
      api.post(`/postcard-slots/${s.id}/rebook-hold`, { business_id: b.id })
        .then(({ data }) => toast.success(`Spot held for you through ${new Date(data.reserved_until).toLocaleDateString()} — complete payment to lock it in.`))
        .catch((e) => toast.error(e?.response?.data?.detail || "That spot couldn't be held — it may have just been taken."));
    }
  };

  const pickBusiness = (v) => {
    const b = myBiz.find((x) => x.id === v);
    setForm((f) => ({
      ...f,
      business_id: v,
      deal_headline: f.deal_headline || b?.ai_deal_headline || "",
      deal_details: f.deal_details || b?.ai_deal_details || "",
    }));
  };

  const checkout = async () => {
    if (!form.business_id) return toast.error("Choose a business");
    if (!form.terms) return toast.error("Please accept the terms to continue");
    if (!form.tos) return toast.error("Please accept the Terms of Service to continue");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/postcard-slot", {
        slot_id: slot.id, business_id: form.business_id,
        deal_headline: form.deal_headline, deal_details: form.deal_details,
        ab: form.ab || null,
        plan: form.plan || "per_drop",
        terms_accepted: !!form.terms,
        terms_version: TERMS_VERSION,
        consent_saved_payment: !!form.consent,
        origin_url: window.location.origin,
        affiliate_id: window.affiliateId || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="rounded-3xl bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream p-8 sm:p-10 mb-8">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-amber"><Mail className="h-4 w-4" />Premium Postcard Mailer</span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold">Pick your ad space on the postcard</h1>
        <p className="mt-3 text-sam-cream/70 max-w-2xl">Each 9×12 landscape postcard is shared by 12 local companies and mailed to 10,000 homes. Tap any open spot on the front or back below to reserve it.</p>
        <div className="mt-5 flex flex-wrap gap-4 text-sm">
          <div className="rounded-lg bg-white/10 px-4 py-2"><span className="font-mono font-bold">{money(1800)}</span> · 4×9 Premium ×1</div>
          <div className="rounded-lg bg-white/10 px-4 py-2"><span className="font-mono font-bold">{money(1300)}</span> · 4×6 Standard ×2</div>
          <div className="rounded-lg bg-white/10 px-4 py-2"><span className="font-mono font-bold">{money(900)}</span> · 4×3 Basic ×9</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h2 className="font-display text-xl font-bold text-sam-navy">Choose a route</h2>
        <div className="flex items-center gap-3">
          <Link to="/coverage" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-sam-navy hover:gap-2.5 transition-all" data-testid="postcards-coverage-link"><MapPin className="h-4 w-4" />Coverage</Link>
          <Link to="/calculator" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-sam-crimson hover:gap-2.5 transition-all" data-testid="postcards-roi-link">ROI Calculator <ArrowRight className="h-4 w-4" /></Link>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-52" data-testid="postcard-state-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All active states</SelectItem>
              {states.map((s) => <SelectItem key={s.abbr} value={s.abbr}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="h-48 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>
      ) : runs.length === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center text-sam-slate">No postcard routes here yet — start one in your ZIP below.</div>
      ) : (
        <>
          {/* Route chips */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="postcard-runs">
            {runs.map((r) => (
              <button key={r.id} onClick={() => setActiveRunId(r.id)} data-testid={`run-${r.route_code}`}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${activeRunId === r.id ? "border-sam-crimson bg-sam-crimson text-white" : "border-border bg-card text-sam-navy hover:border-sam-crimson"}`}>
                <MapPin className="h-3.5 w-3.5" />{r.city}, {r.state_abbr}
                <span className={`text-xs font-mono ${activeRunId === r.id ? "text-white/80" : "text-sam-slate"}`}>· {r.available_count > 0 ? `${r.available_count} open` : "full"}</span>
              </button>
            ))}
          </div>

          {/* Selected postcard */}
          {loadingCard ? (
            <div className="h-64 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>
          ) : activeRun ? (
            <div className="grid lg:grid-cols-[1fr_300px] gap-8">
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <div>
                    <span className="font-mono text-xs text-sam-slate">{activeRun.route_code}</span>
                    <h3 className="font-display text-2xl font-extrabold text-sam-navy flex items-center gap-2"><MapPin className="h-5 w-5 text-sam-crimson" />{activeRun.city}, {activeRun.state_abbr}</h3>
                    <p className="text-sm text-sam-slate">{activeRun.metro} · {(activeRun.mailer_count || 0).toLocaleString()} homes mailed</p>
                  </div>
                  <Badge className="bg-sam-green text-base px-3 py-1">{(activeRun.slots || []).filter((s) => s.status === "available").length} of 12 spots open</Badge>
                </div>
                <PostcardCanvas slots={activeRun.slots} onSelect={onSelect} selectable run={activeRun} />
              </div>
              <aside className="space-y-4">
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="font-display font-bold text-sam-navy mb-3">How it works</h3>
                  <ol className="space-y-3 text-sm text-sam-slate list-decimal list-inside">
                    <li>Tap an open A–L spot on the postcard.</li>
                    <li>Pick your business & write your deal + QR headline.</li>
                    <li>Pay securely — that spot is locked to you.</li>
                  </ol>
                  <div className="mt-4 space-y-1.5 text-sm">
                    {["premium", "standard", "basic"].map((t) => {
                      const s = (activeRun.slots || []).find((x) => x.slot_type === t);
                      const price = s ? s.price : { premium: 1800, standard: 1300, basic: 900 }[t];
                      return <div key={t} className="flex justify-between"><span className="text-sam-slate">{TYPE_LABEL[t]}</span><span className="font-mono font-bold text-sam-navy">{money(price)}</span></div>;
                    })}
                  </div>
                  {activeRun.category_locks && Object.keys(activeRun.category_locks).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border text-xs" data-testid="taken-categories">
                      <span className="font-semibold text-sam-navy">Categories already claimed on this route:</span>
                      <span className="text-sam-slate"> {Object.keys(activeRun.category_locks).join(", ")}</span>
                    </div>
                  )}
                </div>
                {!user && <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={() => openAuth("owner")} data-testid="postcards-login-cta">Log in to reserve a spot</Button>}
                {user && myBiz.length === 0 && <Button variant="outline" className="w-full" asChild><Link to="/dashboard">Add a business to advertise</Link></Button>}
              </aside>
            </div>
          ) : null}
        </>
      )}

      <div className="mt-8 rounded-2xl border-2 border-dashed border-sam-crimson/40 bg-sam-crimson/5 p-6" data-testid="start-zip-card">
        <h3 className="font-display text-lg font-extrabold text-sam-navy flex items-center gap-2"><Mail className="h-5 w-5 text-sam-crimson" />Don't see your area? Start a card in your ZIP</h3>
        <p className="mt-1 text-sm text-sam-slate max-w-2xl">Be the first business on a new 9×12 card mailed to 10,000 homes in your ZIP. Claim your spot now and a local Saving America Money rep will help fill the other spaces. A card mails once it has 10+ paid spots and $10,500+ in sales; if it falls short by the deadline, your paid spot carries over to the next drop.</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Input value={newZip} onChange={(e) => setNewZip(e.target.value.replace(/\D/g, "").slice(0, 5))} onKeyDown={(e) => { if (e.key === "Enter") startZip(); }} placeholder="Your ZIP, e.g. 65616" inputMode="numeric" className="w-48 bg-white" data-testid="start-zip-input" />
          <Button className="bg-sam-crimson hover:bg-sam-crimson/90" onClick={startZip} disabled={startBusy} data-testid="start-zip-btn">{startBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowRight className="h-4 w-4 mr-1" />}Start a card in my ZIP</Button>
          {!user && <span className="text-xs text-sam-slate">You'll log in first.</span>}
        </div>
      </div>

      {/* Reserve dialog */}
      <Dialog open={!!slot} onOpenChange={(o) => !o && setSlot(null)}>
        <DialogContent data-testid="reserve-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Reserve {slot && TYPE_LABEL[slot.slot_type]} — {slot && money(slot.price)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Business</Label>
              <Select value={form.business_id} onValueChange={pickBusiness}>
                <SelectTrigger data-testid="reserve-business-select"><SelectValue placeholder="Choose your business" /></SelectTrigger>
                <SelectContent>{myBiz.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Deal headline</Label><Input value={form.deal_headline} onChange={(e) => setForm({ ...form, deal_headline: e.target.value })} placeholder="e.g. $50 off any repair" data-testid="reserve-headline" />
              {(() => { const b = myBiz.find((x) => x.id === form.business_id); return b?.ai_deal_headline && form.deal_headline === b.ai_deal_headline; })() && (
                <p className="mt-1 text-xs text-sam-crimson flex items-center gap-1" data-testid="reserve-ai-prefill-hint"><Sparkles className="h-3 w-3" />Pre-filled from your AI-generated listing deal</p>
              )}
            </div>
            {(() => { const b = myBiz.find((x) => x.id === form.business_id); const deals = b?.ai_deals || []; return deals.length > 0 && (
              <div className="rounded-lg border border-sam-navy/15 bg-sam-cream/40 p-3" data-testid="deal-library">
                {(() => { const winner = b?.ab_winner; const wd = winner && deals.find((d) => d.ab === winner); return wd && (
                  <div className="mb-2 rounded-md border border-sam-green/40 bg-sam-green/10 p-2.5" data-testid="winner-reuse-suggestion">
                    <div className="text-[11px] font-bold text-sam-navy flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-sam-green" />Variation {winner} won more QR redemptions last time</div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-xs text-sam-slate truncate">{wd.name || wd.headline}</span>
                      <Button type="button" size="sm" className="h-7 bg-sam-green hover:bg-sam-green/90 shrink-0"
                        onClick={() => setForm((f) => ({ ...f, deal_headline: wd.headline || "", deal_details: wd.details || "", ab: wd.ab || null }))}
                        data-testid="winner-reuse-btn">Reuse winner</Button>
                    </div>
                  </div>
                ); })()}
                <div className="text-xs font-bold text-sam-navy mb-2 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-sam-crimson" />Your saved AI deals — {deals.length > 1 ? "drag to reorder, tap to use" : "tap to use"}</div>
                <div className="space-y-1.5">
                  {deals.map((d) => (
                    <div key={d.id} className={`flex items-center gap-2 group rounded-md transition-opacity ${dragId === d.id ? "opacity-40" : ""}`}
                      draggable={editDealId !== d.id}
                      onDragStart={() => setDragId(d.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => reorderDeals(b, dragId, d.id)}
                      data-testid={`deal-row-${d.id}`}>
                      {editDealId !== d.id && (
                        <GripVertical className="h-3.5 w-3.5 text-sam-slate/50 cursor-grab shrink-0" data-testid={`deal-grip-${d.id}`} />
                      )}
                      {editDealId === d.id ? (
                        <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRename(d.id); if (e.key === "Escape") setEditDealId(null); }}
                          onBlur={() => saveRename(d.id)} data-testid={`deal-name-input-${d.id}`}
                          className="flex-1 h-8 text-xs" placeholder="Name this deal (e.g. Spring Special)" />
                      ) : (
                        <button type="button" onClick={() => setForm((f) => ({ ...f, deal_headline: d.headline || "", deal_details: d.details || "", ab: d.ab || null }))}
                          data-testid={`deal-chip-${d.id}`}
                          className={`flex-1 text-left text-xs rounded-md border px-2.5 py-1.5 transition-colors ${form.deal_headline === d.headline ? "border-sam-crimson bg-sam-crimson/10 text-sam-navy font-semibold" : "border-border bg-white text-sam-slate hover:border-sam-crimson/50"}`}>
                          <span className="font-semibold text-sam-navy inline-flex items-center gap-1.5">
                            {d.ab && <span className={`inline-grid place-items-center h-4 w-4 rounded text-[10px] font-bold text-white ${d.ab === "A" ? "bg-sam-navy" : "bg-sam-crimson"}`} data-testid={`deal-ab-badge-${d.id}`}>{d.ab}</span>}
                            {d.ab && b?.ab_winner === d.ab && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-sam-green/15 text-sam-green px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide" data-testid={`deal-winner-badge-${d.id}`} title="Best QR redemptions in your last A/B test"><Trophy className="h-2.5 w-2.5" />Proven winner</span>
                            )}
                            {d.name || d.headline || "(no headline)"}
                          </span>
                          {d.name && d.headline && d.name !== d.headline && <span className="block text-[11px] text-sam-slate/70 truncate">{d.headline}</span>}
                        </button>
                      )}
                      {editDealId !== d.id && (
                        <div className="flex gap-0.5 shrink-0">
                          {["A", "B"].map((tag) => (
                            <button key={tag} type="button" onClick={() => setDealAB(d.id, d.ab === tag ? null : tag)}
                              data-testid={`deal-ab-${tag}-${d.id}`} title={`Tag as variation ${tag}`}
                              className={`h-6 w-6 grid place-items-center rounded-md text-[11px] font-bold border transition-colors ${d.ab === tag ? (tag === "A" ? "bg-sam-navy text-white border-sam-navy" : "bg-sam-crimson text-white border-sam-crimson") : "border-sam-navy/20 text-sam-slate hover:border-sam-navy/50"}`}>{tag}</button>
                          ))}
                        </div>
                      )}
                      <button type="button" onClick={() => startRename(d)} data-testid={`deal-rename-${d.id}`}
                        className="shrink-0 h-6 w-6 grid place-items-center rounded-md text-sam-slate hover:text-sam-navy hover:bg-sam-navy/10" title="Rename deal">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => duplicateDeal(d.id)} data-testid={`deal-dup-${d.id}`}
                        className="shrink-0 h-6 w-6 grid place-items-center rounded-md text-sam-slate hover:text-sam-navy hover:bg-sam-navy/10" title="Duplicate deal">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => deleteDeal(d.id)} data-testid={`deal-del-${d.id}`}
                        className="shrink-0 h-6 w-6 grid place-items-center rounded-md text-sam-slate hover:text-sam-crimson hover:bg-sam-crimson/10" title="Remove deal">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ); })()}
            <Button type="button" variant="outline" size="sm" className="w-full border-sam-navy/30" onClick={genDeal} disabled={aiBusy} data-testid="ai-deal-btn">
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1 text-sam-crimson" />Generate deal with AI</>}
            </Button>
            <div><Label>Deal details</Label><Textarea value={form.deal_details} onChange={(e) => setForm({ ...form, deal_details: e.target.value })} placeholder="Fine print, e.g. Mention Saving America Money." data-testid="reserve-details" /></div>
            <div>
              <Label>Commitment plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger data-testid="reserve-plan-select"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}{p.months > 1 ? ` · ${p.months} months` : ""}</SelectItem>)}</SelectContent>
              </Select>
              {(() => { const p = plans.find((x) => x.key === form.plan); return p?.tagline && <p className="mt-1 text-xs text-sam-slate">{p.tagline}</p>; })()}
            </div>
            <label className="flex items-start gap-2 text-xs text-sam-slate cursor-pointer" data-testid="reserve-terms-label">
              <input type="checkbox" checked={!!form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} data-testid="reserve-terms-check" className="mt-0.5 h-4 w-4 accent-sam-crimson" />
              <span>I agree each drop must be paid in full <b>15 days before print/mail</b>. Missing a payment reopens my spot, releases my category, and removes plan bonuses — my account then falls back to the $29/month online plan.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-sam-slate cursor-pointer" data-testid="reserve-consent-label">
              <input type="checkbox" checked={!!form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="reserve-consent-check" className="mt-0.5 h-4 w-4 accent-sam-crimson" />
              <span>Save my card and authorize <b>Saving America Money</b> to charge the $29/month account fallback if I miss a drop payment. (Optional)</span>
            </label>
            <TermsAgree checked={form.tos} onChange={(v) => setForm({ ...form, tos: v })} testid="reserve-tos" />
            <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90" onClick={checkout} disabled={busy || !form.terms || !form.tos} data-testid="reserve-pay-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Pay {slot && money(slot.price)} &amp; Reserve
            </Button>
            {STRIPE_TEST_MODE && <p className="text-xs text-center text-muted-foreground">Test card 4242 4242 4242 4242 · any future date · any CVC</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
